/**
 * HS256 JWTs, once, for everyone.
 *
 * WHY THIS IS IN `shared/` AND NOT IN THE WORKER. The Worker mints the admin's token and
 * the sidecar verifies it. Two implementations of the same algorithm are two chances to
 * disagree about base64url padding, about whether `exp` is inclusive, or about clock
 * leeway - and the failure mode is "every login works in dev and 401s in production".
 * One module, one test suite, both sides import it.
 *
 * It stays honest to `shared/`'s no-runtime-dependency rule: WebCrypto is a platform
 * global present in workerd, in Node 22 and in browsers, not an npm package. `btoa` and
 * `atob` are likewise global in all three.
 *
 * WHAT IS DELIBERATELY NOT HERE: asymmetric signing. The three verifiers (sidecar,
 * PostgREST, tests) all sit inside the same deployment and already share secrets through
 * the same channel, so HS256 buys the same security with one fewer moving part. PostgREST
 * is configured `jwt-secret` + `jwt-secret-is-base64 = false`, i.e. a raw symmetric
 * secret, which is what {@link signJwt} consumes.
 */
import { isEditorJwtClaims, type EditorJwtClaims } from './auth';

/** The only algorithm this module speaks. Anything else in a header is rejected. */
export const JWT_ALGORITHM = 'HS256';

/**
 * Tolerance applied to `exp` and `nbf`, in seconds.
 *
 * The Worker's clock and the sidecar's clock are different machines. Thirty seconds is
 * small enough that an expired token is useless in practice and large enough that NTP
 * jitter never logs an admin out mid-request.
 */
export const JWT_CLOCK_LEEWAY_SECONDS = 30;

export type JwtErrorCode =
  | 'malformed'
  | 'unsupported-algorithm'
  | 'bad-signature'
  | 'expired'
  | 'not-yet-valid'
  | 'wrong-issuer'
  | 'wrong-audience'
  | 'bad-claims';

/**
 * Every rejection is this one type with a machine-readable `code`.
 *
 * Callers map the code to an HTTP status; they must NOT put `message` in a response body
 * aimed at a browser, because distinguishing "bad signature" from "expired" to an
 * unauthenticated caller is a small oracle. The sidecar logs the code and answers 401.
 */
export class JwtError extends Error {
  constructor(
    readonly code: JwtErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'JwtError';
  }
}

// --------------------------------------------------------------------- base64url

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/*
 * The `<ArrayBuffer>` argument is not decoration. Under the DOM lib, `BufferSource`
 * excludes a SharedArrayBuffer-backed view, so a bare `Uint8Array` (whose default is
 * `ArrayBufferLike`) will not pass to `crypto.subtle.verify` - the app's typecheck fails
 * where the worker's and the sidecar's do not. Pinning the backing store here keeps this
 * module compiling in all three runtimes.
 */
export function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function encodeSegment(value: unknown): string {
  return bytesToBase64Url(encoder.encode(JSON.stringify(value)));
}

function decodeSegment(segment: string): unknown {
  return JSON.parse(decoder.decode(base64UrlToBytes(segment)));
}

// ------------------------------------------------------------------------ signing

/*
 * The return type is inferred rather than written as `Promise<CryptoKey>`: `CryptoKey` is
 * a global in workerd and in the browser but is not in scope for a `lib: ES2023` +
 * `types: node` program, and naming it here would make this package typecheck only in
 * some of the three runtimes that consume it.
 */
async function hmacKey(secret: string, usage: 'sign' | 'verify') {
  return crypto.subtle.importKey('raw', encoder.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, [usage]);
}

/** Signs an arbitrary claim set. The caller owns `iat`/`exp`; nothing is added here. */
export async function signJwt(claims: Readonly<Record<string, unknown>>, secret: string): Promise<string> {
  if (secret.length === 0) throw new JwtError('bad-claims', 'refusing to sign with an empty secret');
  const signingInput = `${encodeSegment({ alg: JWT_ALGORITHM, typ: 'JWT' })}.${encodeSegment(claims)}`;
  const signature = await crypto.subtle.sign('HMAC', await hmacKey(secret, 'sign'), encoder.encode(signingInput));
  return `${signingInput}.${bytesToBase64Url(new Uint8Array(signature))}`;
}

export interface VerifyOptions {
  /** Required `iss`. Omit to accept any issuer (the harness does; production does not). */
  readonly issuer?: string;
  /** Required `aud`. Omit to accept any audience. */
  readonly audience?: string;
  /** Epoch SECONDS. Injectable so expiry is testable without faking the clock. */
  readonly now?: number;
  readonly leewaySeconds?: number;
}

/**
 * Verifies signature then registered claims, and returns the raw payload.
 *
 * Order matters: nothing about the payload is believed until the MAC checks out, so an
 * unsigned token can never steer the issuer/audience comparison. Signature comparison is
 * `crypto.subtle.verify`, which is constant time - a hand-rolled string compare here
 * would be a timing oracle on the MAC.
 */
export async function verifyJwt(token: string, secret: string, options: VerifyOptions = {}): Promise<Record<string, unknown>> {
  const parts = token.split('.');
  if (parts.length !== 3) throw new JwtError('malformed', `expected 3 dot-separated segments, got ${parts.length}`);
  const [headerSegment, payloadSegment, signatureSegment] = parts as [string, string, string];

  let header: unknown;
  let payload: unknown;
  try {
    header = decodeSegment(headerSegment);
    payload = decodeSegment(payloadSegment);
  } catch {
    throw new JwtError('malformed', 'header or payload is not base64url-encoded JSON');
  }

  const alg = (header as Record<string, unknown> | null)?.['alg'];
  // Pinned, not read: accepting the token's own `alg` is the classic JWT confusion bug,
  // and "none" is the classic exploit of it.
  if (alg !== JWT_ALGORITHM) throw new JwtError('unsupported-algorithm', `header alg is ${JSON.stringify(alg)}, expected ${JWT_ALGORITHM}`);

  let signature: Uint8Array<ArrayBuffer>;
  try {
    signature = base64UrlToBytes(signatureSegment);
  } catch {
    throw new JwtError('malformed', 'signature segment is not base64url');
  }
  const ok = await crypto.subtle.verify('HMAC', await hmacKey(secret, 'verify'), signature, encoder.encode(`${headerSegment}.${payloadSegment}`));
  if (!ok) throw new JwtError('bad-signature', 'signature does not match');

  if (typeof payload !== 'object' || payload === null) throw new JwtError('bad-claims', 'payload is not an object');
  const claims = payload as Record<string, unknown>;

  const now = options.now ?? Math.floor(Date.now() / 1000);
  const leeway = options.leewaySeconds ?? JWT_CLOCK_LEEWAY_SECONDS;

  const exp = claims['exp'];
  if (typeof exp !== 'number') throw new JwtError('bad-claims', 'exp is required and must be a number');
  if (now - leeway >= exp) throw new JwtError('expired', `expired at ${exp}, now ${now}`);

  const nbf = claims['nbf'];
  if (typeof nbf === 'number' && now + leeway < nbf) throw new JwtError('not-yet-valid', `not valid before ${nbf}, now ${now}`);

  if (options.issuer !== undefined && claims['iss'] !== options.issuer) {
    throw new JwtError('wrong-issuer', `iss is ${JSON.stringify(claims['iss'])}, expected ${options.issuer}`);
  }
  if (options.audience !== undefined && !audienceMatches(claims['aud'], options.audience)) {
    throw new JwtError('wrong-audience', `aud is ${JSON.stringify(claims['aud'])}, expected ${options.audience}`);
  }

  return claims;
}

/** RFC 7519 allows `aud` to be a string or an array of strings; both are honoured. */
function audienceMatches(aud: unknown, expected: string): boolean {
  if (typeof aud === 'string') return aud === expected;
  if (Array.isArray(aud)) return aud.includes(expected);
  return false;
}

// ------------------------------------------------------------- the editor's token

/** Mints the admin's session token. The Worker is the only caller in production. */
export async function signEditorJwt(claims: EditorJwtClaims, secret: string): Promise<string> {
  return signJwt(claims as unknown as Record<string, unknown>, secret);
}

/**
 * Verifies AND narrows to {@link EditorJwtClaims}. This is what the sidecar calls: a
 * signature check that hands back `Record<string, unknown>` would leave every consumer
 * re-validating the shape, and one of them would forget.
 */
export async function verifyEditorJwt(token: string, secret: string, options: VerifyOptions = {}): Promise<EditorJwtClaims> {
  const claims = await verifyJwt(token, secret, options);
  if (!isEditorJwtClaims(claims)) throw new JwtError('bad-claims', 'payload is not a well-formed EditorJwtClaims');
  return claims;
}

/**
 * Reads the payload WITHOUT verifying anything.
 *
 * Only the browser may call this, and only to decide what to grey out. The name is long
 * and ugly on purpose. The database is the authority: a tampered claim changes what the
 * SPA draws and changes nothing about what Postgres will return.
 */
export function decodeJwtPayloadWithoutVerifying(token: string): Record<string, unknown> | null {
  const segment = token.split('.')[1];
  if (segment === undefined) return null;
  try {
    const payload = decodeSegment(segment);
    return typeof payload === 'object' && payload !== null ? (payload as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** `Authorization: Bearer <token>` -> `<token>`, or null when the header is absent/odd. */
export function bearerToken(headerValue: string | null | undefined): string | null {
  if (typeof headerValue !== 'string') return null;
  const match = /^Bearer[ ]+(?<token>[A-Za-z0-9._~+/-]+=*)$/.exec(headerValue.trim());
  return match?.groups?.['token'] ?? null;
}
